import React, { useState, useEffect } from "react";
import { Card, Space, Typography, Tag, Descriptions, Alert } from "antd";
import {
  ApiOutlined,
  CheckCircleOutlined,
  DisconnectOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface PicqerConnectionStatusProps {
  picqerEnabled?: boolean;
  picqerFulfilmentCustomerId?: number;
  fulfilmentCustomerName?: string;
}

export const PicqerConnectionStatus: React.FC<PicqerConnectionStatusProps> = ({
  picqerEnabled,
  picqerFulfilmentCustomerId,
  fulfilmentCustomerName: providedCustomerName,
}) => {
  const { t } = useTranslation();
  const [fulfilmentCustomerName, setFulfilmentCustomerName] = useState<
    string | null
  >(providedCustomerName || null);

  const isConnected = picqerEnabled && picqerFulfilmentCustomerId;

  useEffect(() => {
    // Simply show the customer ID since we can't safely fetch the name
    // without exposing other companies' data
    if (isConnected && picqerFulfilmentCustomerId && !providedCustomerName) {
      // Don't fetch customer name to avoid privacy violations
      setFulfilmentCustomerName(`Customer ${picqerFulfilmentCustomerId}`);
    }
  }, [isConnected, picqerFulfilmentCustomerId, providedCustomerName]);

  return (
    <Card
      title={
        <Space>
          <ApiOutlined />
          {t("picqerConnection.title", "Picqer Integration")}
        </Space>
      }
      size="small"
      style={{ marginTop: 16 }}
    >
      {isConnected ? (
        <div>
          <Alert
            message={t(
              "picqerConnection.connected",
              "Picqer Integration Active"
            )}
            description={t(
              "picqerConnection.connectedDescription",
              "Your company is successfully connected to Picqer for product management and fulfillment operations."
            )}
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 16 }}
          />

          <Descriptions column={1} size="small">
            <Descriptions.Item
              label={t("picqerConnection.status", "Connection Status")}
            >
              <Tag color="green" icon={<CheckCircleOutlined />}>
                {t("picqerConnection.active", "Active")}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item
              label={t(
                "picqerConnection.fulfilmentCustomer",
                "Fulfilment Customer"
              )}
            >
              <Text>{fulfilmentCustomerName || "Unknown"}</Text>
            </Descriptions.Item>
          </Descriptions>
        </div>
      ) : (
        <div>
          <Alert
            message={t(
              "picqerConnection.notConnected",
              "No Picqer Integration"
            )}
            description={t(
              "picqerConnection.notConnectedDescription",
              "Your company is not currently connected to Picqer. Contact your administrator to set up the integration."
            )}
            type="warning"
            showIcon
            icon={<DisconnectOutlined />}
          />
        </div>
      )}
    </Card>
  );
};
